import React, { useEffect, useState } from 'react';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import SmoothImage from '@ff/ui/SmoothImage';
import zIndices from 'client/utils/zIndices';
import { Swiper, SwiperSlide } from 'client/shared/Swiper';
import type { Swiper as SwiperProp } from 'swiper';

const imageHeight = '100vh';

type Props = {
  currentImageIndex: number | boolean;
  images: {
    src: string;
    scaleRatio: number,
    objectPosition: string,
  }[];
  swiperNavigationColor?: string;
  height?: string;
  paginationEnabled: boolean;
  backgroundColor?: string;
}

const SwiperPagination = {
  clickable: true,
  renderBullet(index, className) {
    return `<span class="${className}"></span>`;
  }
}

export default function SmoothSliderImageV2({
  currentImageIndex,
  images,
  swiperNavigationColor,
  paginationEnabled,
  backgroundColor='white',
}: Props) {
  const [swiper, setSwiper] = useState<SwiperProp>();
  useEffect(() => {
    if(currentImageIndex !== false){
      swiper?.slideTo(currentImageIndex as number + 1);
    }
  }, [currentImageIndex, swiper]);

  return (
    <SwiperContainer
      color={swiperNavigationColor}
      className="modelSwiper"
      effect="fade"
      loop={true}
      pagination={paginationEnabled ? SwiperPagination : false}
      navigation={{
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev'
      }}
      onSwiper={(s) => setSwiper(s)}
    >
      {images?.map((element) => (
        // TODO:
        // Fix - Encountered two children with the same key
        // Add key to fragment to prevent image flickering
        <React.Fragment key={element.src}>
          <SwiperSlide>
            <SwiperSlideContent>
              <ImageContainer backgroundColor={backgroundColor as any}>
                <StyledSmoothImage
                  alt="Faraday Future"
                  aspectRatio="16/9"
                  objectPosition={element.objectPosition}
                  desktopHeight={imageHeight}
                  duration={0.6}
                  height={imageHeight}
                  scaleRatio={element.scaleRatio}
                  src={element.src}
                />
              </ImageContainer>
            </SwiperSlideContent>
          </SwiperSlide>
        </React.Fragment>
      ))}
      <div className="swiper-button-prev"></div>
      <div className="swiper-button-next"></div>
    </SwiperContainer>
  );
}

const SwiperContainer = styled(Swiper)(({ theme, color }) => ({
  // maxHeight: '100%',
  width: '100%',
  maxHeight: imageHeight,
  zIndex: zIndices['swiper'],
  '.swiper-slide': {
    /**
     * Safari: Fix image flicker on swiper slide transition
     * https://github.com/nolimits4web/swiper/issues/3527#issuecomment-771536449
     */
    WebkitBackfaceVisibility: 'hidden',
    WebkitTransform: 'translate3d(0,0,0)'
  },
  '.swiper-wrapper': {
    WebkitTransformStyle: 'preserve-3d'
  },
  '.swiper-pagination': {
    bottom: '2em !important' // position of carousel dots
  },
  '.swiper-pagination-bullet-active': {
    background: `${color} !important`
  },
  '.swiper-pagination-bullet': {
    background: color,
    width: '10px',
    height: '10px',
    visibility: 'visible',

    [theme.mediaQueries.md]: {
      visibility: 'hidden',
    },
  },
  '.swiper-pagination-bullet:only-child': {
    visibility: 'hidden'
  },
  '.swiper-button-prev': {
    visibility: 'hidden',// don't show navigation button in mobile page
    width: '28px',
    height: '48px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: '1px solid rgba(0, 0, 0, 0.2)',
    borderRadius: '1px',

    color: color,
    left: '52px',
    '::after': {
      fontSize: '16px'
    },

    [theme.mediaQueries.md]: {
      visibility: 'visible',
    },
  },
  '.swiper-button-next': {
    visibility: 'hidden',// don't show navigation button in mobile page
    width: '28px',
    height: '48px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: '1px solid rgba(0, 0, 0, 0.2)',

    color: color,
    right: '52px',
    ':after': {
      fontSize: '16px'
    },

    [theme.mediaQueries.md]: {
      visibility: 'visible',
    },
  },
}));

const SwiperSlideContent = styled(SwiperSlide)({
  // Use important to override SwiperSlide styles
  display: 'flex',
  justifyContent: 'center',
  maxHeight: '100%',
  width: '100%',
  objectFit: 'cover',
  overflow: 'hidden',
  height: '100% !important',
});

const ImageContainer = styled(Box)({
  width: '100%',
  height: '100%',
});

type ImgProps = {
  isLoading?: boolean;
  scaleRatio?: number;
  aspectRatio?: string;
  desktopHeight?: string;
  mobileHeight?: string;
  objectPosition?: string;
}

const StyledSmoothImage = styled(SmoothImage)<ImgProps>(({ theme, isLoading, scaleRatio = 1, aspectRatio = '1/1', desktopHeight, objectPosition }) => ({
  aspectRatio: aspectRatio,
  objectFit: scaleRatio > 1 ? 'contain' : 'cover',
  objectPosition,
  transform: `scale(${scaleRatio})`,
  visibility: isLoading ? 'hidden' : 'visible',
  width: '100%',
  height: '325px',
  maxHeight: '325px',
  [theme.mediaQueries.md]: {
    height: desktopHeight || '100%',
    maxHeight: desktopHeight || '100%',
  }
}));
